import { useEffect } from "react";

const INTERACTIVE_SELECTOR = [
    "button",
    "a",
    "input",
    "textarea",
    "select",
    "[role='button']",
    "[contenteditable='true']",
    ".btn",
    ".btn-close",
].join(",");

const getNumberData = (element: HTMLElement, key: string) => {
    const value = Number(element.dataset[key] || 0);
    return Number.isFinite(value) ? value : 0;
};

const clamp = (value: number, min: number, max: number) => {
    if (min > max) {
        return value;
    }

    return Math.min(Math.max(value, min), max);
};

const GlobalDraggableModals = () => {
    useEffect(() => {
        let activeDialog: HTMLElement | null = null;
        let startPointerX = 0;
        let startPointerY = 0;
        let startOffsetX = 0;
        let startOffsetY = 0;
        let minOffsetX = 0;
        let maxOffsetX = 0;
        let minOffsetY = 0;
        let maxOffsetY = 0;

        const stopDragging = () => {
            activeDialog?.classList.remove("co2-modal-dialog-dragging");
            activeDialog = null;
        };

        const handlePointerMove = (event: PointerEvent) => {
            if (!activeDialog) {
                return;
            }

            event.preventDefault();

            const nextX = clamp(
                startOffsetX + event.clientX - startPointerX,
                minOffsetX,
                maxOffsetX
            );
            const nextY = clamp(
                startOffsetY + event.clientY - startPointerY,
                minOffsetY,
                maxOffsetY
            );

            activeDialog.dataset.co2DragX = String(nextX);
            activeDialog.dataset.co2DragY = String(nextY);
            const assistantShiftX = getNumberData(activeDialog, "co2AssistantShiftX");
            activeDialog.style.transform = `translate3d(${nextX + assistantShiftX}px, ${nextY}px, 0)`;
        };

        const handlePointerDown = (event: PointerEvent) => {
            if (event.button !== 0) {
                return;
            }

            const target = event.target as HTMLElement | null;
            if (!target || target.closest(INTERACTIVE_SELECTOR)) {
                return;
            }

            const header = target.closest(".modal-header");
            const dialog = header?.closest(".modal-dialog") as HTMLElement | null;
            const modal = dialog?.closest(".modal") as HTMLElement | null;

            if (!header || !dialog || !modal || !modal.classList.contains("show")) {
                return;
            }

            const content = dialog.querySelector(".modal-content") as HTMLElement | null;
            const rect = (content || dialog).getBoundingClientRect();
            const edgePadding = 0;

            startPointerX = event.clientX;
            startPointerY = event.clientY;
            startOffsetX = getNumberData(dialog, "co2DragX");
            startOffsetY = getNumberData(dialog, "co2DragY");

            minOffsetX = startOffsetX - rect.left + edgePadding;
            maxOffsetX = startOffsetX + window.innerWidth - rect.right - edgePadding;
            minOffsetY = startOffsetY - rect.top + edgePadding;
            maxOffsetY = startOffsetY + window.innerHeight - rect.bottom - edgePadding;

            activeDialog = dialog;
            activeDialog.classList.add("co2-modal-dialog-dragging");

            event.preventDefault();
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("pointermove", handlePointerMove, { passive: false });
        document.addEventListener("pointerup", stopDragging);
        document.addEventListener("pointercancel", stopDragging);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", stopDragging);
            document.removeEventListener("pointercancel", stopDragging);
        };
    }, []);

    return null;
};

export default GlobalDraggableModals;
